import React from 'react'
import { Form, Input, Select } from 'antd'
import { identifierTypes } from '@dentolize/clinic-mobile/src/shared/utils/helpers'
import { useTranslation } from 'react-i18next'
import * as PropTypes from 'prop-types'
import { useUser } from '../../../context/userContext'

export const getIdentifierFormatError = ({ value, identifierType, nationality, country, companyCountry }) => {
  if (!value || !value.trim()) {
    return null
  }

  const isSA = country === 'SA' || companyCountry === 'SA'
  const isEG = country === 'EG' || companyCountry === 'EG'

  if (isSA) {
    if (identifierType === 'national_id') {
      if (
        !/^\d{10}$/.test(value) ||
        (nationality === 'SA' && !value.startsWith('1')) ||
        (nationality !== 'SA' && !value.startsWith('2'))
      ) {
        return { key: 'app.nationalIDNotValid' }
      }
    } else if (identifierType === 'residency' || identifierType === 'iqama') {
      if (!/^\d{10}$/.test(value) || !value.startsWith('2')) {
        return { key: 'dhs.residencyNotValid', message: 'Residency/Iqama number must be 10 digits and start with 2' }
      }
    }
  }

  if (isEG && identifierType === 'national_id') {
    if (!/^\d{14}$/.test(value)) {
      return { message: 'Egyptian ID must be 14 digits' }
    }

    const century = value.charAt(0)
    const month = parseInt(value.slice(3, 5), 10)
    const day = parseInt(value.slice(5, 7), 10)

    if (!['2', '3'].includes(century) || month < 1 || month > 12 || day < 1 || day > 31) {
      return { message: 'Invalid century digit in Egyptian ID' }
    }
  }

  return null
}

const SAUDI_ID_TYPES = ['national_id', 'residency']

// An ID number has no spaces; one typed or pasted in made a valid ID fail the e-invoice checks
const withoutSpaces = value => (typeof value === 'string' ? value.replace(/\s+/g, '') : value)

/** Saudi national IDs (1…) and iqama numbers (2…) are 10 digits ending in a Luhn check digit. */
const hasValidSaudiIdCheckDigit = id => {
  const sum = [...id].reduce((total, char, index) => {
    const digit = Number(char) * (index % 2 === 0 ? 2 : 1)
    return total + (digit > 9 ? digit - 9 : digit)
  }, 0)
  return sum % 10 === 0
}

/**
 * Whether a national ID or iqama number entered in Saudi Arabia is not a real one. Saving it is
 * allowed; ZATCA would flag it, so it is left off 15% e-invoices and a 0% invoice is refused.
 */
export const isInvalidSaudiId = ({ value, identifierType, country }) => {
  const id = String(value || '').trim()
  if (country !== 'SA' || !id || !SAUDI_ID_TYPES.includes(identifierType || 'national_id')) return false
  return !/^[12]\d{9}$/.test(id) || !hasValidSaudiIdCheckDigit(id)
}

const NationalIdField = ({
  requiredFields,
  handleCheckDuplicate,
  identifierType,
  nationality,
  companyCountry,
  forceNationalIDCheck
}) => {
  const { user } = useUser()
  const { t } = useTranslation()

  return (
    <Form.Item
      label={t('app.nationalId')}
      name="nationalId"
      dependencies={['identifierType']}
      normalize={withoutSpaces}
      rules={[
        {
          validator: async (_, value) => {
            if (!value || !value.trim()) {
              if (requiredFields.includes('nationalId')) {
                return Promise.reject(new Error(t('app.nationalIdRequired')))
              } else {
                return Promise.resolve()
              }
            }

            if (value.length > 100) {
              return Promise.reject(new Error(t('auth.nameLength')))
            }

            if (!user?.company?.extraSettings?.forceNationalIDCheck && !forceNationalIDCheck) {
              return Promise.resolve()
            }

            const formatError = getIdentifierFormatError({
              value,
              identifierType,
              nationality,
              country: user?.company?.country,
              companyCountry
            })

            if (formatError) {
              return Promise.reject(
                new Error(formatError.key ? t(formatError.key, formatError.message) : formatError.message)
              )
            }

            return Promise.resolve()
          }
        },
        {
          warningOnly: true,
          validator: async (_, value) =>
            isInvalidSaudiId({ value, identifierType, country: user?.company?.country || companyCountry })
              ? Promise.reject(new Error(t('app.saudiIdInvalidWarning')))
              : Promise.resolve()
        }
      ]}
    >
      <Input
        onBlur={e => handleCheckDuplicate && handleCheckDuplicate(e, '**')}
        maxLength={100}
        addonBefore={
          <Form.Item
            label={t('common.type')}
            noStyle
            name="identifierType"
            rules={[{ required: true, message: t('app.fieldRequired') }]}
          >
            <Select
              data-testid="patientForm-identifierType"
              style={{ minWidth: 150, marginRight: 4 }}
              options={identifierTypes.map(value => ({ value, label: t(value) }))}
            />
          </Form.Item>
        }
      />
    </Form.Item>
  )
}

NationalIdField.propTypes = {
  requiredFields: PropTypes.array.isRequired,
  handleCheckDuplicate: PropTypes.func,
  identifierType: PropTypes.string,
  nationality: PropTypes.string,
  companyCountry: PropTypes.string,
  forceNationalIDCheck: PropTypes.bool
}

export default NationalIdField
